import { getVisitByVisitIdV2 } from '@mummssoftware/utils/pimReader';
import { timestampToDisplay } from '@mummssoftware/utils/formatters';
import { ifString } from '@mummssoftware/utils/utils';
import { InProgress, InProgressDriving } from '@mummssoftware/utils/constants';
import { flowRight as compose, property } from 'lodash';
import { colors } from './constants';
import {
  VisitAttributes,
  completed,
  inProgress,
  inProgressDriving,
  patientVisitLog,
  visitLog,
} from '../../translations/en.json';

export const getVisitStatusLabel = (status: string) => {
  if (status === InProgressDriving) return inProgressDriving;
  if (status === InProgress) return inProgress;
  return completed;
};

export const getEditableAttributes = (visit: any, attributes: any) => {
  const visitAttributes = JSON.parse(visit.visitDetails?.visitattrsumdto);
  if (
    (visitAttributes && visitAttributes[0] === null) ||
    visitAttributes === null
  )
    return attributes;
  const selectableAttributes = visitAttributes.filter(
    (att: any) => att.selectable && att.ispatienttype && att.uiavailable,
  );
  return selectableAttributes.sort(sortAlphaByName);
};

// JSON attributes => attributes sorted alphabetically by name
const parseAttributes = (visit: any) =>
  (visit.visitattributes && JSON.parse(visit.visitattributes)) ||
  (visit.visitattrsumdto && JSON.parse(visit.visitattrsumdto)) ||
  [];

export const sortAlphaByName = (a: any, b: any) => {
  if (a && b) {
    if (a?.name < b?.name) {
      return -1;
    } else if (a?.name < b?.name) {
      return 1;
    }
    return 0;
  }
  return 0;
};

export const getFilterableAttributes = (visits: any) =>
  visits
    ?.reduce((acc: any, visit: any) => {
      const attributes = parseAttributes(visit);
      attributes.forEach((att: any) => {
        if (
          att &&
          att.isselected &&
          att.ispatienttype &&
          !acc.find((cachedAtt: any) => cachedAtt.id === att.id)
        )
          acc.push({ ...att, hex: colors[acc.length]?.hex });
      });
      return acc;
    }, [])
    .sort(sortAlphaByName);

export const filterVisitsByAttribute = (patientVisits: any, attributes: any) =>
  patientVisits &&
  patientVisits.filter((visit: any) => {
    const visitAttributes = parseAttributes(visit);
    return (
      (visitAttributes &&
        visitAttributes.find(
          (att: any) =>
            att?.isselected && attributes.find((a: any) => att.id === a.id),
        )) ||
      false
    );
  });

export const getFilteredAttributes = (pimAttributes: any, editingIds: any) =>
  (pimAttributes &&
    pimAttributes
      .filter((pimAtt: any) =>
        editingIds.find((editId: any) => editId === pimAtt.id),
      )
      .sort(sortAlphaByName)) ||
  [];

export const toggleAttributesColumn = (gridAPI: any, bool: boolean) =>
  gridAPI && gridAPI.columnController.setColumnVisible([VisitAttributes], bool);

export const formatAttributesCamel = (attributes: any) =>
  attributes &&
  attributes[0] &&
  attributes.reduce((seed: any, att: any) => {
    const newAtt: any = {
      id: att.id,
      name: att.name,
      isSelected: att.isselected,
      selectable: att.selectable,
      uiavailable: att.uiavailable,
      shortname: att.shortname,
      isnonpatienttype: att.isnonpatienttype,
      ispatienttype: att.ispatienttype,
    };
    seed.unshift(newAtt);
    return seed;
  }, []);

export const formatAttributesLowercase = (attributes: any) =>
  attributes &&
  attributes[0] &&
  attributes.reduce((seed: any, att: any) => {
    const newAtt: any = {
      id: att.id,
      name: att.name,
      isselected: att.isSelected,
      selectable: att.selectable,
      uiavailable: att.uiavailable,
      shortname: att.shortname,
      isnonpatienttype: att.isnonpatienttype,
      ispatienttype: att.ispatienttype,
    };
    seed.unshift(newAtt);
    return seed;
  }, []);

const updateAttributes = (visit: any, edittedAttributes: any) =>
  visit &&
  visit.visitAttributes.map((att: any) => {
    const editedAtt = edittedAttributes.find((a: any) => a.id === att.id);
    if (!editedAtt) return att;
    att.isSelected = editedAtt.isSelected;
    return att;
  });

/**
 * update VISIT mileage, time, fees ...
 */
export const updatePastPatientVisit = async (
  pim: any,
  editedVisit: any,
  visit: any,
  agency: string,
  keycloak: any,
  toggleF2F: any,
  // todo HCPCS necessary here?
  hcpcsDTO?: mumms.HCPCSDTO[],
) => {
  const pimVisit = await pim.getPatientVisit(visit.patient_id, visit, agency);
  if (pimVisit instanceof Response) {
    return null;
  }
  let updatedPriorVisit = { ...pimVisit, ...editedVisit };
  if (!editedVisit.startDate) {
    updatedPriorVisit = { ...updatedPriorVisit, startDate: pimVisit.startDate };
  }
  if (hcpcsDTO) {
    updatedPriorVisit.physicianServices = hcpcsDTO;
  }

  if (!updatedPriorVisit.visitType) {
    updatedPriorVisit.visitType = pimVisit.visitType;
  }

  if (pimVisit.isFaceToFaceAllowed && toggleF2F !== null) {
    updatedPriorVisit.faceToFaceVisit = toggleF2F;
  }
  const updatedVisitRes = await pim.updatePatientVisit(
    visit.patientnumber,
    updatedPriorVisit as any,
    agency,
  );

  if (updatedVisitRes instanceof Response) {
    const responseData = await updatedVisitRes.json();
    return responseData;
  }
  // return visit id
  const newVisit = await getVisitByVisitIdV2(
    String(visit.id),
    agency,
    keycloak,
  );
  if (newVisit instanceof Response) {
    return null;
  }
  return newVisit;
};

/**
 * update visitAttributes icon A
 */

export const updateVisitAttribute = async (
  pim: any,
  editingAttributes: any,
  visit: any,
  agency: any,
) => {
  const pimVisit = await pim.getPatientVisit(visit.patient_id, visit, agency);
  if (pimVisit instanceof Response) {
    return null;
  }

  pimVisit.visitAttributes = updateAttributes(pimVisit, editingAttributes);

  const updatedVisit = await pim.updatePatientVisit(
    visit.patientnumber,
    pimVisit as any,
    agency,
  );
  if (updatedVisit instanceof Response) {
    return null;
  }
  return updatedVisit;
};
/**
 * update visit HCPCS
 */

export const updateVisitHCPCS = async (
  pim: any,
  patientNumber: string,
  personNumber: string,
  pimVisit: any,
  agency: any,
  keycloak: any,
  hcpcsDTO?: mumms.HCPCSDTO[],
) => {
  if (hcpcsDTO) {
    pimVisit.physicianServices = hcpcsDTO;
  }
  const updatedVisit = await pim.updatePatientVisit(
    patientNumber,
    pimVisit as any,
    agency,
  );
  if (updatedVisit instanceof Response) {
    return null;
  }
  const newVisit = await getVisitByVisitIdV2(
    String(pimVisit.id),
    agency,
    keycloak,
  );
  if (newVisit instanceof Response) {
    return null;
  }
  return newVisit;
};

export const getAppTitle = (patient: any, providerInfo: any) =>
  (patient && patientVisitLog) ||
  `${visitLog} ${
    providerInfo && ` - ${providerInfo?.firstname} ${providerInfo?.lastname}`
  }`;

export const getDisplayDate = (fld: string) =>
  compose(ifString(timestampToDisplay), property(fld));
